import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Trophy, Loader2, FileText, Plus, Lock, Send, CheckCircle2, RefreshCw, Trash2, Unlock,
  IndianRupee, Presentation, AlertTriangle, Ban, Users, Pencil, Target, IndianRupee as Rupee,
} from 'lucide-react';
import { fmtDate } from './demoConstants';
import { PageHeader, Stat, EmptyState } from './DemoUI';
import DemoDrilldownModal from './DemoDrilldownModal';
import DemoPeriodSelector from './DemoPeriodSelector';
import DemoDetailModal from './DemoDetailModal';
import DemoCategoryTab from './DemoCategoryTab';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const STATUS = {
  preview: { label: 'Live preview', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  submitted: { label: 'Submitted', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  approved: { label: 'Approved · locked', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  paid: { label: 'Paid', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};

const Pill = ({ s }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${STATUS[s]?.cls || STATUS.draft.cls}`} data-testid="statement-status">
    {s === 'approved' || s === 'paid' ? <Lock className="w-2.5 h-2.5" /> : null}{STATUS[s]?.label || s}
  </span>
);

export default function DemoIncentivePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);         // loaded statement or live preview
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState(null);
  const [periodLabel, setPeriodLabel] = useState('');
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ title: '', notes: '' });
  const [drill, setDrill] = useState(null);
  const [tab, setTab] = useState('statement');
  const [openId, setOpenId] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const loadList = useCallback(async () => {
    try {
      const r = await api.get('/training/demos/incentive/list');
      setList(r.data.statements || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load statements'); }
  }, []);

  const loadPreview = useCallback(async () => {
    if (!range) return;
    setLoading(true);
    try {
      const r = await api.get('/training/demos/incentive/preview', { params: range });
      setSel({ ...r.data, status: 'preview' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load the live preview'); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const openStatement = async (id) => {
    setLoading(true);
    try {
      const r = await api.get(`/training/demos/incentive/${id}`);
      setSel(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to open the statement'); }
    finally { setLoading(false); }
  };

  const act = async (fn, okMsg, reload = true) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      await loadList();
      if (reload && sel?.id) await openStatement(sel.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/training/demos/incentive/generate', { ...range, ...gen });
      toast.success('Statement frozen — numbers will not move from here');
      setGenOpen(false);
      setGen({ title: '', notes: '' });
      await loadList();
      setSel(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate the statement'); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!sel?.id) return;
    try {
      const r = await api.get(`/training/demos/incentive/${sel.id}/pdf`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `incentive-statement-${sel.date_from}_${sel.date_to}.pdf`;
      a.click();
    } catch { toast.error('PDF export failed'); }
  };

  const t = sel?.totals || {};
  const rows = sel?.rows || [];
  const live = sel?.status === 'preview';
  const locked = sel?.status === 'approved' || sel?.status === 'paid';
  const demoRows = sel?.demo_rows || [];
  const openDrill = (title, subtitle, list_, variant = 'revenue') => setDrill({ title, subtitle, rows: list_, variant });

  return (
    <div className="space-y-8" data-testid="incentive-page">
      <PageHeader icon={Trophy} eyebrow="Trainer Demo" title="Incentive Statement"
        subtitle="A frozen snapshot management signs off on — top trainer by revenue, plus the top counsellor by demos conducted and by conversion."
        testid="incentive-header">
        <Button variant="outline" size="sm" className="h-10" onClick={loadPreview} disabled={busy} data-testid="incentive-live-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Live preview
        </Button>
        <Button size="sm" className="h-10 bg-violet-600 hover:bg-violet-700 text-white" disabled={!range} onClick={() => setGenOpen(true)} data-testid="incentive-generate-btn">
          <Plus className="w-4 h-4 mr-1.5" />Freeze statement
        </Button>
      </PageHeader>

      <Card className="p-4 border flex flex-wrap items-center gap-3" data-testid="incentive-range-bar">
        <DemoPeriodSelector
          defaultKey="this_month"
          onChange={(r) => { setRange({ date_from: r.from_date, date_to: r.to_date }); setPeriodLabel(r.label || ''); }}
        />
        <p className="text-[11.5px] text-slate-500 flex-1 min-w-[240px]">
          Today, yesterday, this week / month / quarter / FY, a specific month, or any custom range — whatever management asks for.
        </p>
      </Card>

      <div className="flex items-center gap-1.5 border-b border-slate-200" data-testid="incentive-tabs">
        {[['statement', 'Statement'], ['technology', 'By Technology']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${
              tab === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid={`incentive-tab-${k}`}>{l}</button>
        ))}
        <span className="ml-auto text-[11px] text-slate-400 pb-2">
          {tab === 'statement' ? 'The signed-off snapshot' : 'Live split by the Books category behind each course'}
        </span>
      </div>

      {tab === 'technology' ? (
        <DemoCategoryTab embedded fixedPeriod={range} />
      ) : (<>
      <Card className="p-5 border border-sky-200 bg-sky-50/60" data-testid="freeze-explainer">
        <p className="text-[12.5px] font-semibold text-sky-900 flex items-center gap-2"><Lock className="w-4 h-4" />What “Freeze statement” does — and why it matters</p>
        <ul className="mt-2 space-y-1.5 text-[12px] text-sky-900/90 list-disc pl-5">
          <li><b>Live preview</b> recalculates from Zoho Books every time you open it. Useful to look at, <b>dangerous to pay on</b> — the numbers keep moving.</li>
          <li><b>Freeze</b> takes a permanent copy of this period: every admission, the invoice behind it, the amount, GST split, credit notes as of right now, and the trainer / counsellor ranking. That copy is <b>never recalculated</b>.</li>
          <li>So if a credit note is raised next month against a June invoice, <b>June's approved statement does not change</b>. Instead <b>Check variance</b> lists exactly what moved since the freeze, with the rupee delta — your clawback list.</li>
          <li>The PDF is produced from the frozen copy, so the document management signed and the data behind it can never drift apart.</li>
          <li>Flow: <b>Draft</b> (manager prepares) → <b>Submitted</b> → <b>Approved &amp; locked</b> (admin) → <b>Paid</b>. An admin can still regenerate or reopen a statement with a reason — every action is recorded in the audit trail.</li>
        </ul>
      </Card>

      {list.length > 0 && (
        <section className="space-y-2" data-testid="incentive-list-section">
          <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Saved statements ({list.length})</h2>
          <Card className="border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                  <th className="px-4 py-3">Statement</th><th className="px-4 py-3">Period</th><th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Admissions</th><th className="px-4 py-3 text-right">Revenue excl. GST</th>
                  <th className="px-4 py-3">Approved by</th><th className="px-4 py-3">Variance</th><th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.id} className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors ${sel?.id === s.id ? 'bg-violet-50' : ''}`} onClick={() => openStatement(s.id)} data-testid={`statement-row-${s.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-800">{s.title}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(s.date_from)} – {fmtDate(s.date_to)}</td>
                      <td className="px-4 py-3"><Pill s={s.status} /></td>
                      <td className="px-4 py-3 text-right tabular-nums">{s.totals?.admissions ?? 0}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">{inr(s.totals?.ex_gst_total)}</td>
                      <td className="px-4 py-3 text-slate-600">{s.approved_by || '—'}</td>
                      <td className="px-4 py-3">
                        {s.variance?.count
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">{s.variance.count} changed · {inr(s.variance.total_delta)}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isAdmin && (
                          <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${s.title}"? This cannot be undone.`)) act(() => api.delete(`/training/demos/incentive/${s.id}`), 'Statement deleted', false).then(() => { if (sel?.id === s.id) loadPreview(); }); }}
                            className="text-slate-400 hover:text-rose-600" data-testid={`statement-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
      ) : !sel ? (
        <Card className="border"><EmptyState title="Nothing to show" hint="Pick a date range to see the live preview." /></Card>
      ) : (
        <div className="space-y-6" data-testid="statement-body">
          <Card className="p-5 border flex flex-wrap items-center justify-between gap-3" data-testid="statement-toolbar">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-bold text-slate-900">{sel.title || 'Live preview'}</h2>
                <Pill s={sel.status} />
              </div>
              <p className="text-[12px] text-slate-500 mt-0.5">
                {fmtDate(sel.date_from)} – {fmtDate(sel.date_to)}{live && periodLabel ? ` · ${periodLabel}` : ''}
                {sel.generated_by ? ` · frozen by ${sel.generated_by}` : ' · recalculated live from Books'}
                {sel.approved_by ? ` · approved by ${sel.approved_by}` : ''}
                {sel.edited_by ? ` · last edited by ${sel.edited_by}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!live && <Button variant="outline" size="sm" className="h-9" onClick={downloadPdf} data-testid="statement-pdf"><FileText className="w-3.5 h-3.5 mr-1.5" />PDF</Button>}
              {!live && <Button variant="outline" size="sm" className="h-9" disabled={busy} onClick={() => act(() => api.post(`/training/demos/incentive/${sel.id}/recheck-variance`), 'Variance re-checked')} data-testid="statement-variance"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Check variance</Button>}
              {!live && sel.status === 'draft' && <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700 text-white" disabled={busy} onClick={() => act(() => api.post(`/training/demos/incentive/${sel.id}/submit`), 'Submitted for approval')} data-testid="statement-submit"><Send className="w-3.5 h-3.5 mr-1.5" />Submit</Button>}
              {!live && isAdmin && (sel.status === 'draft' || sel.status === 'submitted') && <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => act(() => api.post(`/training/demos/incentive/${sel.id}/approve`, {}), 'Approved and locked')} data-testid="statement-approve"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Approve &amp; lock</Button>}
              {!live && isAdmin && sel.status === 'approved' && <Button size="sm" className="h-9 bg-violet-600 hover:bg-violet-700 text-white" disabled={busy} onClick={() => act(() => api.post(`/training/demos/incentive/${sel.id}/mark-paid`), 'Marked paid')} data-testid="statement-paid"><Rupee className="w-3.5 h-3.5 mr-1.5" />Mark paid</Button>}
              {!live && isAdmin && <Button variant="outline" size="sm" className="h-9" disabled={busy} onClick={() => act(() => api.put(`/training/demos/incentive/${sel.id}`, { regenerate: true }), 'Rows regenerated from current data')} data-testid="statement-regenerate"><Pencil className="w-3.5 h-3.5 mr-1.5" />Regenerate rows</Button>}
              {!live && isAdmin && locked && <Button variant="outline" size="sm" className="h-9 border-rose-200 text-rose-700" onClick={() => setReopenOpen(true)} data-testid="statement-reopen"><Unlock className="w-3.5 h-3.5 mr-1.5" />Reopen</Button>}
            </div>
          </Card>

          {locked && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12.5px] text-emerald-900 flex items-start gap-2" data-testid="statement-locked-note">
              <Lock className="w-4 h-4 mt-0.5 shrink-0" />
              <span>These figures are <b>frozen</b> as of {String(sel.generated_at || '').slice(0, 10)} and will not change when Books data changes. Use <b>Check variance</b> to see what moved since.</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="leaderboard">
            <Card className="p-5 border border-emerald-200 bg-emerald-50/60 cursor-pointer hover:shadow-md transition"
              onClick={() => sel.top_trainer && openDrill(sel.top_trainer.name, `Top trainer by revenue excl. GST · ${inr(sel.top_trainer.ex_gst_total)}`, rows.filter((r) => r.trainer_name === sel.top_trainer.name))}
              data-testid="top-trainer-card">
              <p className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" />Top trainer · by admission revenue excl. GST</p>
              <p className="text-2xl font-bold text-slate-900 mt-1.5">{sel.top_trainer?.name || '—'}</p>
              <p className="text-[12.5px] text-emerald-800 mt-0.5">{inr(sel.top_trainer?.ex_gst_total)} excl. GST from {sel.top_trainer?.admissions ?? 0} admission(s) · {sel.top_trainer?.demos ?? 0} demo(s)</p>
              <p className="text-[11.5px] text-emerald-700/80">{inr(sel.top_trainer?.invoice_total)} including GST</p>
            </Card>
            {(() => {
              const byDemos = sel.top_counsellor_demos || sel.top_counsellor;
              const byConv = sel.top_counsellor_conversion;
              return (
                <>
                  <Card className="p-5 border border-violet-200 bg-violet-50/60 cursor-pointer hover:shadow-md transition"
                    onClick={() => byDemos && openDrill(byDemos.name, `Top counsellor by demos conducted · ${byDemos.valid_demos} demo(s)`, rows.filter((r) => r.counsellor_name === byDemos.name))}
                    data-testid="top-counsellor-card">
                    <p className="text-[10.5px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" />Top counsellor · by demos conducted</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1.5">{byDemos?.name || '—'}</p>
                    <p className="text-[12.5px] text-violet-800 mt-0.5">{byDemos?.valid_demos ?? 0} valid demo(s) · {byDemos?.admissions ?? 0} admission(s)</p>
                    <p className="text-[11.5px] text-violet-700/80">{byDemos?.conversion_pct ?? 0}% conversion on {byDemos?.approved ?? 0} approved demo(s)</p>
                  </Card>
                  <Card className="p-5 border border-amber-200 bg-amber-50/60 cursor-pointer hover:shadow-md transition"
                    onClick={() => byConv && openDrill(byConv.name, `Top counsellor by conversion · ${byConv.conversion_pct}%`, rows.filter((r) => r.counsellor_name === byConv.name))}
                    data-testid="top-counsellor-conversion-card">
                    <p className="text-[10.5px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />Top counsellor · by conversion</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1.5">{byConv?.name || '—'}</p>
                    <p className="text-[12.5px] text-amber-800 mt-0.5">{byConv?.conversion_pct ?? 0}% · {byConv?.admissions ?? 0} admission(s) from {byConv?.approved ?? 0} approved demo(s)</p>
                    <p className="text-[11.5px] text-amber-700/80">
                      {byConv?.min_base > 1 ? `Counsellors with at least ${byConv.min_base} approved demos only` : 'Too few demos for a fair base — every counsellor counted'}
                    </p>
                  </Card>
                </>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="incentive-kpi-rail">
            <Stat icon={Presentation} label="Valid Demos" value={t.valid_demos ?? 0} tone="violet" sub={`${t.void_demos ?? 0} void excluded`} onClick={() => openDrill('Valid demos', 'Every non-void demo counted in this statement', demoRows.filter((r) => !r.void), 'demos')} testid="ikpi-valid" />
            <Stat icon={CheckCircle2} label="Admissions" value={t.admissions ?? 0} tone="emerald" onClick={() => openDrill('Admissions', 'Converted demos counted in this statement', rows)} testid="ikpi-admissions" />
            <Stat icon={Rupee} label="Revenue excl. GST" value={inr(t.ex_gst_total)} tone="emerald" sub={`${t.invoiced ?? 0} invoice(s)`} onClick={() => openDrill('Invoiced admissions', 'Invoice value excluding GST — the incentive base', rows.filter((r) => r.invoice_id))} testid="ikpi-ex-gst" />
            <Stat icon={IndianRupee} label="Incl. GST" value={inr(t.invoice_total)} tone="slate" sub={`GST ${inr(t.tax_total)}`} onClick={() => openDrill('Invoiced admissions', 'Gross invoice value including GST', rows.filter((r) => r.invoice_id))} testid="ikpi-value" />
            <Stat icon={AlertTriangle} label="CN excl. GST" value={inr(t.cn_ex_gst_total)} tone="rose" onClick={() => openDrill('Credited invoices', 'Credit notes applied — clawback candidates', rows.filter((r) => r.cn_applied > 0))} testid="ikpi-cn" />
            <Stat icon={IndianRupee} label="Pending Collection" value={inr(t.balance_total)} tone="amber" sub="raised, not yet collected" onClick={() => openDrill('Pending collection', 'Admission invoices with a balance — shown for information, not deducted', rows.filter((r) => r.balance > 0.5))} testid="ikpi-pending" />
            <Stat icon={Ban} label="Awaiting Invoice" value={t.awaiting_invoice ?? 0} tone="amber" onClick={() => openDrill('Awaiting invoice', 'No invoice counted yet — see the flags for invoices dated before the demo', rows.filter((r) => !r.invoice_id))} testid="ikpi-awaiting" />
            <Stat icon={Users} label="Void Demos" value={t.void_demos ?? 0} tone="slate" sub="excluded from every figure" onClick={() => openDrill('Void demos', 'Marked void by a manager — excluded from all totals', demoRows.filter((r) => r.void), 'demos')} testid="ikpi-void" />
          </div>

          {t.needs_review > 0 && (
            <button onClick={() => openDrill('Invoice dated before demo', 'Within 15 days before the demo = counted (verify); older = not counted (pick if correct)', rows.filter((r) => r.needs_review))}
              className="w-full text-left rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 hover:bg-amber-100 transition-colors" data-testid="incentive-needs-review">
              {t.needs_review} admission(s) have an invoice dated before the demo — click to review.
            </button>
          )}

          {sel.variance?.count ? (
            <Card className="border border-rose-200 overflow-hidden" data-testid="variance-panel">
              <div className="px-4 py-3 bg-rose-50 border-b border-rose-200 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[12.5px] font-semibold text-rose-900">Changed since this statement was frozen — {sel.variance.count} row(s), net {inr(sel.variance.total_delta)} excl. GST</p>
                <p className="text-[11px] text-rose-700">Checked {String(sel.variance.checked_at || '').slice(0, 10)} by {sel.variance.checked_by}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-white text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                    <th className="px-4 py-2.5">Demo</th><th className="px-4 py-2.5">Trainer</th><th className="px-4 py-2.5">Student</th>
                    <th className="px-4 py-2.5">Change</th><th className="px-4 py-2.5">Detail</th>
                    <th className="px-4 py-2.5 text-right">Was</th><th className="px-4 py-2.5 text-right">Now</th><th className="px-4 py-2.5 text-right">Delta</th>
                  </tr></thead>
                  <tbody>
                    {sel.variance.changes.map((v, i) => (
                      <tr key={`${v.demo_id}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-rose-50/50" data-testid={`variance-row-${v.demo_id}`}>
                        <td className="px-4 py-2.5"><button onClick={() => setOpenId(v.demo_id)} className="font-mono text-[11px] text-violet-700 hover:underline">{v.demo_code}</button></td>
                        <td className="px-4 py-2.5 text-slate-700">{v.trainer_name}</td>
                        <td className="px-4 py-2.5 text-slate-700">{v.student_name}</td>
                        <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">{String(v.type).replace('_', ' ')}</span></td>
                        <td className="px-4 py-2.5 text-slate-600">{v.detail}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{inr(v.old_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{inr(v.new_amount)}</td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${v.delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{v.delta < 0 ? '-' : '+'}{inr(Math.abs(v.delta))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : null}

          <section className="space-y-2">
            <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Trainer ranking · by admission revenue excl. GST</h2>
            <Card className="border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                    <th className="px-4 py-3">#</th><th className="px-4 py-3">Trainer</th><th className="px-4 py-3 text-right">Demos</th>
                    <th className="px-4 py-3 text-right">Admissions</th><th className="px-4 py-3 text-right">Conv %</th>
                    <th className="px-4 py-3 text-right">Revenue excl. GST</th><th className="px-4 py-3 text-right">Incl. GST</th><th className="px-4 py-3 text-right">CN excl. GST</th>
                    <th className="px-4 py-3 text-right">Net excl. GST</th><th className="px-4 py-3 text-right">Pending</th><th className="px-4 py-3 text-right">Avg Ticket</th>
                  </tr></thead>
                  <tbody>
                    {(sel.trainers || []).map((g) => (
                      <tr key={g.name} onClick={() => openDrill(g.name, `${g.admissions} admission(s) · ${inr(g.ex_gst_total)} excl. GST`, rows.filter((r) => r.trainer_name === g.name))}
                        className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors ${g.rank === 1 ? 'bg-emerald-50/60' : g.cn_total > 0 ? 'bg-rose-50/40' : ''}`} data-testid={`incentive-trainer-row-${g.id || g.name}`}>
                        <td className="px-4 py-3 font-bold text-slate-500">{g.rank}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">{g.name}{g.rank === 1 ? <Trophy className="w-3 h-3 inline ml-1.5 text-emerald-600" /> : null}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{g.demos ?? 0}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">{g.admissions}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{g.conversion_pct ?? 0}%</td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(g.ex_gst_total)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-500">{inr(g.invoice_total)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-rose-700">{g.cn_ex_gst_total ? `-${inr(g.cn_ex_gst_total)}` : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(g.net_ex_gst_total)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums ${g.balance_total > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{g.balance_total > 0 ? inr(g.balance_total) : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{inr(g.avg_ticket)}</td>
                      </tr>
                    ))}
                    {(sel.trainers || []).length === 0 && <tr><td colSpan={11} className="px-4 py-6 text-center text-[12px] text-slate-400">No demos in this range.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>

          <section className="space-y-2">
            <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Counsellor ranking · by valid (non-void) demos</h2>
            <Card className="border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                    <th className="px-4 py-3">#</th><th className="px-4 py-3">Counsellor</th><th className="px-4 py-3 text-right">Valid Demos</th>
                    <th className="px-4 py-3 text-right">Void</th><th className="px-4 py-3 text-right">Approved</th>
                    <th className="px-4 py-3 text-right">Admissions</th><th className="px-4 py-3 text-right">Conv %</th>
                    <th className="px-4 py-3 text-right">Adm. Value excl. GST</th><th className="px-4 py-3 text-right">Incl. GST</th>
                    <th className="px-4 py-3 text-right">CN excl. GST</th><th className="px-4 py-3 text-right">Net excl. GST</th>
                    <th className="px-4 py-3 text-right">Pending</th><th className="px-4 py-3 text-right">Avg Ticket</th>
                    <th className="px-4 py-3 text-right">FB Missing</th>
                  </tr></thead>
                  <tbody>
                    {(sel.counsellors || []).map((c) => (
                      <tr key={c.name} onClick={() => openDrill(c.name, `${c.valid_demos} valid demo(s) · ${c.admissions} admission(s) · ${inr(c.admission_value)} excl. GST`, rows.filter((r) => r.counsellor_name === c.name))}
                        className={`border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors ${c.rank === 1 ? 'bg-violet-50/60' : c.cn_total > 0 ? 'bg-rose-50/40' : ''}`} data-testid={`incentive-counsellor-row-${c.id || c.name}`}>
                        <td className="px-4 py-3 font-bold text-slate-500">{c.rank}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">{c.name}{c.rank === 1 ? <Trophy className="w-3 h-3 inline ml-1.5 text-violet-600" /> : null}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{c.valid_demos}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-400">{c.void_demos || '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{c.approved}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">{c.admissions}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-violet-700">{c.conversion_pct}%</td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(c.admission_value)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-500">{inr(c.admission_value_gross)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-rose-700">{c.cn_ex_gst_total ? `-${inr(c.cn_ex_gst_total)}` : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(c.net_ex_gst_total)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums ${c.balance_total > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{c.balance_total > 0 ? inr(c.balance_total) : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{inr(c.avg_ticket)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums ${c.fb_missing ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>{c.fb_missing || '—'}</td>
                      </tr>
                    ))}
                    {(sel.counsellors || []).length === 0 && <tr><td colSpan={14} className="px-4 py-6 text-center text-[12px] text-slate-400">No demos in this range.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        </div>
      )}
      </>)}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Freeze an incentive statement</DialogTitle>
            <DialogDescription className="text-[11.5px] text-slate-500">
              {fmtDate(range?.date_from)} – {fmtDate(range?.date_to)}. Every row is stored as it stands right now; later Books changes show up as variance instead of silently altering the payout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[12px] text-slate-600">Title</Label>
              <Input value={gen.title} onChange={(e) => setGen((p) => ({ ...p, title: e.target.value }))} placeholder={`Incentive statement ${range?.date_from || ""} to ${range?.date_to || ""}`} className="h-9 text-[13px]" data-testid="generate-title" />
            </div>
            <div className="space-y-1">
              <Label className="text-[12px] text-slate-600">Notes for management (optional)</Label>
              <Textarea value={gen.notes} onChange={(e) => setGen((p) => ({ ...p, notes: e.target.value }))} rows={3} className="text-[13px]" data-testid="generate-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)} data-testid="generate-cancel">Cancel</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" disabled={busy} onClick={generate} data-testid="generate-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Lock className="w-4 h-4 mr-1.5" />}Freeze statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Reopen this statement</DialogTitle>
            <DialogDescription className="text-[11.5px] text-slate-500">It goes back to Draft and loses its approval. The reason is recorded in the audit trail.</DialogDescription>
          </DialogHeader>
          <Textarea value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={3} placeholder="e.g. invoice cancelled after approval — needs regeneration" className="text-[13px]" data-testid="reopen-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)} data-testid="reopen-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || reopenReason.trim().length < 5}
              onClick={() => act(() => api.post(`/training/demos/incentive/${sel.id}/reopen`, { reason: reopenReason }), 'Statement reopened').then(() => { setReopenOpen(false); setReopenReason(''); })}
              data-testid="reopen-confirm">Reopen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DemoDrilldownModal open={!!drill} onClose={() => setDrill(null)} title={drill?.title} subtitle={drill?.subtitle}
        rows={drill?.rows || []} variant={drill?.variant || 'revenue'} onOpenDemo={(id) => { setDrill(null); setOpenId(id); }} />
      <DemoDetailModal demoId={openId} readOnly onClose={() => setOpenId(null)} />
    </div>
  );
}
